<template>
  <d-container fluid class="main-content-container px-4">
    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="contractors-settlements-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          v-model="filters.order"
          :placeholder="$t('order.orderNumber')"
          class="mr-1 w-50" />

        <v-select
          class="ml-2 w-100"
          :options="contractors"
          v-model="filters.contractor_id"
          :placeholder="$t('contractor.chooseContractor')"
          :reduce="contractor => contractor.id"
          :searchable="false"
          label="name">
        </v-select>

        <v-select
          class="ml-2 w-100"
          :options="[{
            text: $t('contractor.all'),
            value: 0
          }, {
            text: $t('contractor.onlyEnded'),
            value: 1
          }]"
          v-model="filters.only_ended"
          :placeholder="$t('contractor.chooseEndedStatus')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          class="ml-2 w-100"
          :options="[{
            text: $t('contractor.all'),
            value: ''
          }, {
            text: $t('ui.settled'),
            value: 'settled'
          }, {
            text: $t('contractor.toBeSettled'),
            value: 'unsettled'
          }]"
          v-model="filters.settlement_status"
          :placeholder="$t('contractor.chooseSettlementStatus')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>

        <d-button
          v-if="dashboardParam"
          theme="primary"
          class="ml-2 text-nowrap"
          @click.prevent="$bus.$emit('dashboard-contractors-settlements-import-popup-open')">
          <i class="material-icons">cloud_upload</i>
          {{ $t('contractorsSettlementsImportPopup.importXlsx') }}
        </d-button>

        <d-button
          v-if="dashboardParam"
          theme="success"
          class="ml-2 text-nowrap"
          @click.prevent.stop="loadFilteredData(true)">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            <i class="material-icons">cloud_download</i>
            {{ $t('order.downloadXls') }}
          </template>
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('contractor.contractor') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('service.serviceName') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('service.serviceGroup') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('contractor.unitPrice') }}</th>
            <th scope="col" class="border-0" style="width: 100px;">{{ $t('ui.pieces') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('ui.order') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">{{ $t('contractor.dateOfSampleAndAcceptance') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('contractor.serviceCost') }}</th>
            <th scope="col" class="border-0" style="width: 120px">{{ $t('settlements.settlement') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(settlement, index) of contractorsSettlements"
            :key="'settlement-' + index">
            <td class="align-middle">
              {{ settlement.contractor_name }}
            </td>
            <td class="align-middle">
              {{ settlement.name }}
            </td>
            <td class="align-middle">
              {{ settlement.group_name }}
            </td>
            <td class="align-middle">
              {{ Number(settlement.contractor_unit_price).toFixed(2) }} {{ settlement.currency_code }}
            </td>
            <td class="align-middle">
              {{ settlement.quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
            <td class="align-middle">
              {{ settlement.order_name }}
            </td>
            <td class="align-middle">
              <small><strong>{{ $t('service.sample.sampleDate') }}:</strong> {{ settlement.obtaining_at }}</small><br>
              <small><strong>{{ $t('contractor.acceptanceDate') }}:</strong> {{ settlement.accepted_at }}</small><br>
            </td>
            <td class="align-middle">
              {{ Number(settlement.contractor_price).toFixed(2) }} {{ settlement.currency_code }}
            </td>
            <td class="align-middle">
              <d-button
                v-if="settlement.can_settlement"
                theme="primary"
                @click.native="makeSettlement(settlement.id)"
                :disabled="settlementInProgress === settlement.id"
                class="mr-2"
                :title="$t('settlements.settle')">
                <template v-if="settlementInProgress !== settlement.id">{{ $t('settlements.settle') }}</template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>

              <small
                v-else
                class="text-success text-center">
                <strong>{{ $t('settlements.settled') }}:</strong><br>
                {{ settlement.settlement_at }}
              </small>
            </td>
          </tr>
          <tr v-if="contractorsSettlements.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('contractor.noItemsToBeSettled') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import HasFilters from './../../mixins/HasFilters.vue';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './../../components/common/Pagination.vue';

export default {
  name: 'contractors-settlements',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    dashboardParam: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedContractors;
    }
  },
  data() {
    return {
      contractorsSettlements: [],
      contractors: [],
      loaded: false,
      loadedContractors: false,
      loadError: false,
      settlementInProgress: false,
      filters: {
        order: '',
        only_ended: null,
        contractor_id: null,
        settlement_status: null
      },
      downloadInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('contractors-settlements-reload-needed', this.loadFilteredData);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let whereData = {};

      if (this.filters.contractor_id !== null && this.filters.contractor_id > 0) {
        whereData.contractor_id = this.filters.contractor_id;
      }

      if (this.filters.order !== '') {
        whereData.order = this.filters.order;
      }

      if (this.filters.settlement_status && this.filters.settlement_status.value !== '') {
        whereData.settlement_status = this.filters.settlement_status.value;
      }

      if (this.filters.only_ended && this.filters.only_ended.value !== '') {
        whereData.only_ended = this.filters.only_ended.value;
      }

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        whereData['download_excel'] = 1;
        this.downloadInProgress = true;

        this.$http.post('/orders/services/settlements', {
          where: whereData
        }, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('order.ordersXlsFilename') + '.xlsx');
          document.querySelector('.big-popup .modal-body').appendChild(link);
          link.click();
          document.querySelector('.big-popup .modal-body').removeChild(link);
          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        ListUtils.loadPaginatedItemsData(this, {
          endpoint: '/orders/services/settlements?page=' + this.currentPage,
          listField: 'contractorsSettlements',
          loadError: 'loadError',
          method: 'get',
          params: {
            where: whereData
          }
        });
      }
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order: '',
        contractor_id: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/contractors/items',
        loadedKey: 'loadedContractors',
        outputKey: 'contractors',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['contractors-settlements-filters-form'], this.debouncedLoadFilteredData);
    },
    makeSettlement (settlementID) {
      this.settlementInProgress = settlementID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/services/settlement/' + settlementID,
        method: 'get',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('contractor.makeSettlementErrorText'),
        successAction: () => {
          this.settlementInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.settlementInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('contractors-settlements-reload-needed', this.loadFilteredData);
  }
};
</script>
